<template>
    <modal-dialog
        id="project-comparison"
        :show.sync="shown"
        class="uk-modal-dialog-large"
    >
        <template v-slot:title>
            <h2>Comparable Projects</h2>
        </template>

        <div class="uk-grid uk-grid-small">
            <div class="uk-width-1-2">
                <h6 class="uk-margin-bottom-remove">Filters</h6>
                <div class="uk-grid uk-grid-small">
                    <div class="uk-width-2-10 uk-margin-bottom lam-label-column">
                        <div class="uk-form-controls-text">Size Range</div>
                    </div>
                    <div class="uk-width-8-10 uk-margin-bottom">
                        <form-input
                            id="comparing-range"
                            type="number"
                            :value="state.range"
                            name="range"
                            glyph="percent"
                            :required="false"
                            :editable="true"
                            width="1-1"
                            @input="filter_by_range"
                            class="uk-width-3-10"
                        />
                    </div>
                    <div class="uk-width-2-10 uk-margin-bottom lam-label-column">
                        <div class="uk-form-controls-text">Type(s)</div>
                    </div>
                    <div class="uk-width-8-10 uk-margin-bottom">
                        <category-picker
                            id="comparables"
                            :value="state.category_ids"
                            :editable="true"
                            title="Compare to Project Type(s)"
                            subtitle=""
                            :required-sections="[]"
                            @change="toggle_category"
                        />
                    </div>
                    <div class="uk-width-1-1">
                        <div class="uk-grid uk-grid-small uk-grid-width-1-4 uk-margin-top">
                            <form-checkbox
                                v-for="status in statuses"
                                :key="status.id"
                                :id="`comparing-status-${status.id}`"
                                :value="state.except_stati.indexOf(status.code_constant) >= 0"
                                name="except_stati"
                                :true-value="false"
                                :false-value="true"
                                :editable="true"
                                :required="false"
                                @input="toggle_status(status.code_constant, $event)"
                            >
                                {{ status.name }}
                            </form-checkbox>
                        </div>
                    </div>
                </div>
            </div>
            <div class="uk-width-1-2">
                <table v-if="state.query_info&&state.query_info.amount" class="uk-table uk-table-condensed uk-margin-top lam-summary-table">
                    <thead>
                        <tr>
                            <th class="uk-width-1-4">&nbsp;</th>
                            <th class="uk-width-1-4">Min</th>
                            <th class="uk-width-1-4">Avg</th>
                            <th class="uk-width-1-4">Max</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="uk-text-right uk-text-bold lam-table-th">Amount</td>
                            <td>${{ state.query_info.amount.min | nibnut.number("0,0") }}</td>
                            <td>${{ state.query_info.amount.avg | nibnut.number("0,0") }}</td>
                            <td>${{ state.query_info.amount.max | nibnut.number("0,0") }}</td>
                        </tr>
                        <tr>
                            <td class="uk-text-right uk-text-bold lam-table-th">Hours</td>
                            <td>{{ state.query_info.hours.min | nibnut.number("0,0") }} h</td>
                            <td>{{ state.query_info.hours.avg | nibnut.number("0,0") }} h</td>
                            <td>{{ state.query_info.hours.max | nibnut.number("0,0") }}h </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <hr>

        <data-table
            :id="state_identifier"
            :columns="visible_columns"
            :rows="filtered_rows"
            :search="state.search"
            :total="state.total"
            :found="state.found"
            :current-page="state.page"
            :total-pages="nb_pages"
            :searchable="false"
            :can-add="false"
            table-class="lam-records-list"
            empty-title="No comparables found"
            @sort="sort_by"
            @page="goto_page"
        >

            <template
                v-slot:summary="{ row }"
            >
                <h6>{{ row.name }}</h6>
            </template>
            <template
                v-slot:tbody="{ row, field }"
            >
                <router-link
                    v-if="field === 'display_name'"
                    :to="{ name: `${!!row.activated_at?'project':'prospective_project'}.edit`, params: { id: row.id } }"
                >
                    {{ row.display_name }}
                </router-link>
                <span v-else-if="field === 'size'" style="white-space: nowrap;">
                    {{ row.size | nibnut.number("0,0.00") }}
                    <span v-if="row.size_units==='AC'">Acres</span>
                    <span v-if="row.size_units==='SM'">M<sup>2</sup></span>
                    <span v-if="row.size_units==='SF'">Ft<sup>2</sup></span>
                </span>
                <span v-else-if="field.match(/^fee/i)">
                    $
                    {{ row[field] | nibnut.number("0,0") }}
                </span>
                <span v-else-if="field === 'hours'">{{ row[field] | nibnut.number("0,0") }}</span>
                <span v-else>{{ row[field] }}</span>
            </template>
        </data-table>
    </modal-dialog>
</template>

<script type="text/javascript">
import is_remote_data_table_source from "../../mixins/IsRemoteDataTableSource"
import string_utilities from "../../mixins/StringUtilities"

import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import FormInput from "../../ui/FormInput"
import FormCheckbox from "../../ui/FormCheckbox"
import CategoryPicker from "../../ui/CategoryPicker"

export default {
    mixins: [is_remote_data_table_source, string_utilities],
    components: {
        ModalDialog,
        FormInput,
        FormCheckbox,
        CategoryPicker
    },
    mounted () {
        this.reroot()
    },
    watch: {
        show: "maybe_reroot",
        "compareTo.id": "reroot"
    },
    methods: {
        reroot () {
            if(this.show && !!this.compareTo && !!this.compareTo.id) {
                this.set_state_values({
                    context_id: this.compareTo.id,
                    category_ids: this.compareTo.category_ids || [],
                    except_stati: []
                })
                this.reset()
            }
        },
        maybe_reroot () {
            if(this.show) this.reroot()
        },
        filter_by_range (value) {
            this.set_state_value("range", value)
            this.refresh()
        },
        toggle_category (checked, category_id) {
            const category_ids = this.state.category_ids.slice()
            const index = category_ids.indexOf(category_id)
            if(checked && (index < 0)) category_ids.push(category_id)
            else if(!checked && (index >= 0)) category_ids.splice(index, 1)
            this.set_state_value("category_ids", category_ids)
            this.refresh()
        },
        toggle_status (status_id, checked) {
            const except_stati = this.state.except_stati.slice()
            const index = except_stati.indexOf(status_id)
            if(!checked && (index >= 0)) except_stati.splice(index, 1)
            else if(checked && (index < 0)) except_stati.push(status_id)
            this.set_state_value("except_stati", except_stati)
            this.refresh()
        },
        close () {
            this.$emit("update:show", false)
        }
    },
    computed: {
        state_identifier () {
            return "project-comparison"
        },
        shown: {
            get: function () {
                return this.show
            },
            set: function (show) {
                if(!show) this.close()
            }
        },
        visible_columns () {
            const { activated_at_as_string, size_units, ...visible_columns } = this.columns
            return visible_columns
        },
        statuses () {
            return this.dictionary("project_status", "name")
        }
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        compareTo: {
            required: true
        }
    },
    data () {
        return {
            range: this.$root.applicationSettings.comparable_size,

            entity: "project",
            columns: {
                display_name: { label: "Name", sort: null, type: "plain" },
                size: { label: "Size", sort: null, type: "plain" },
                computed_fee: { label: "Amount", sort: null, type: "plain" },
                computed_spent: { label: "Spent", sort: false, type: "plain" },
                hours: { label: "Hours", sort: false, type: "plain" },
                status_label: { label: "Status", sort: false, type: "plain" },
                size_units: { label: "Units", sort: false, type: "plain" },
                activated_at_as_string: { label: "Activated", sort: false, type: "plain" }
            },

            default_state: {
                per_page: 30,
                page: 1,
                sort_by: "name",
                sort_dir: "asc",
                filter_on: null,
                filter: "",
                archived: false,
                search: "",
                range: this.$root.applicationSettings.comparable_size,
                category_ids: [],
                except_stati: [],
                total: 0,
                found: 0,
                context: "comparables",
                context_id: 0
            }
        }
    }
}
</script>
